"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<AssetTone, string>;
    riskLevel: Record<PersonaRisk, string>;
    riskBadge: (risk: string) => string;
    nameWithAge: (name: string, age: string) => string;
    goalWithHorizon: (goal: string, horizon: string) => string;
    personas: { name: string; age: string; situation: string; goal: string; horizon: string }[];
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { equity: "株式", bond: "債券", reit: "REIT", commodity: "コモディティ", cash: "預金" },
    riskLevel: { low: "低め", mid: "中くらい", high: "高め" },
    riskBadge: (risk) => `リスク許容度：${risk}`,
    nameWithAge: (name, age) => `${name}（${age}）`,
    goalWithHorizon: (goal, horizon) => `${goal}（${horizon}）`,
    personas: [
      {
        name: "Aさん",
        age: "55歳",
        situation: "退職資金とは別に余裕資金がある",
        goal: "長く使わない資金を育てたい",
        horizon: "15年以上",
      },
      {
        name: "Bさん",
        age: "38歳",
        situation: "子供の教育費を5年で準備",
        goal: "使う時期が近い資金を守りたい",
        horizon: "約5年",
      },
      {
        name: "Cさん",
        age: "25歳",
        situation: "独身・会社員",
        goal: "長期で増やしたいが、大きな下落は苦手",
        horizon: "20年以上",
      },
    ],
    title: "年齢だけでは、ポートフォリオは決まらない",
    description:
      "年齢は一つの要素にすぎず、目的と本人が許容できる値動きで比率は変わります。丸の大きさが配分の大きさを表しています。",
  },
  en: {
    assets: { equity: "Equities", bond: "Bonds", reit: "REIT", commodity: "Commodities", cash: "Deposits" },
    riskLevel: { low: "Lower", mid: "Medium", high: "Higher" },
    riskBadge: (risk) => `Risk tolerance: ${risk}`,
    nameWithAge: (name, age) => `${name} (${age})`,
    goalWithHorizon: (goal, horizon) => `${goal} (${horizon})`,
    personas: [
      {
        name: "Investor A",
        age: "55",
        situation: "Has spare money separate from retirement savings",
        goal: "Wants to grow money not needed for a long time",
        horizon: "15+ years",
      },
      {
        name: "Investor B",
        age: "38",
        situation: "Preparing school fees over five years",
        goal: "Wants to protect money needed soon",
        horizon: "about 5 years",
      },
      {
        name: "Investor C",
        age: "25",
        situation: "Single, salaried",
        goal: "Wants long-term growth but dislikes large falls",
        horizon: "20+ years",
      },
    ],
    title: "Age alone does not determine a portfolio",
    description:
      "Age is only one input; the weights change with your purpose and the movement you can tolerate. Circle size represents the size of each allocation.",
  },
};

// ── Asset class colors (consistent across all personas) ────────────

type AssetTone = "equity" | "bond" | "reit" | "commodity" | "cash";
type PersonaRisk = "low" | "mid" | "high";

const TONE_COLOR: Record<AssetTone, string> = {
  equity: COLOR.sky, // 株式 → 青
  bond: COLOR.amber, // 債券 → 黄
  reit: "#ec4899", // REIT → ピンク
  commodity: COLOR.emerald, // コモディティ → 緑
  cash: "#71717a", // 預金 → グレー
};

const PERSONA_COLOR = "#64748b";

const PERSONA_RISK_COLOR: Record<PersonaRisk, string> = {
  low: COLOR.rose,
  mid: COLOR.amber,
  high: COLOR.emerald,
};

// ── Data ───────────────────────────────────────────────────────────

type Asset = { tone: AssetTone; weight: number };

type Persona = {
  risk: PersonaRisk;
  assets: Asset[];
};

// 4 asset classes shared, only weights differ
const personas: Persona[] = [
  {
    risk: "high",
    assets: [
      { tone: "equity", weight: 45 },
      { tone: "bond", weight: 10 },
      { tone: "reit", weight: 15 },
      { tone: "commodity", weight: 10 },
      { tone: "cash", weight: 20 },
    ],
  },
  {
    risk: "low",
    assets: [
      { tone: "equity", weight: 15 },
      { tone: "bond", weight: 25 },
      { tone: "reit", weight: 10 },
      { tone: "commodity", weight: 5 },
      { tone: "cash", weight: 45 },
    ],
  },
  {
    risk: "mid",
    assets: [
      { tone: "equity", weight: 30 },
      { tone: "bond", weight: 20 },
      { tone: "reit", weight: 15 },
      { tone: "commodity", weight: 10 },
      { tone: "cash", weight: 25 },
    ],
  },
];

// ── Circle packing ─────────────────────────────────────────────────

type Circle = { x: number; y: number; r: number; asset: Asset };

function packCircles(
  assets: Asset[],
  cx: number,
  cy: number,
  areaW: number,
  areaH: number,
  minR: number,
  maxR: number
): Circle[] {
  const maxWeight = Math.max(...assets.map((a) => a.weight));
  const circles: Circle[] = assets
    .map((asset) => {
      const frac = asset.weight / maxWeight;
      const r = minR + (maxR - minR) * frac;
      return { x: cx, y: cy, r, asset };
    })
    .sort((a, b) => b.r - a.r);

  const angleStep = (Math.PI * 2) / circles.length;
  circles.forEach((c, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const spread = areaW * 0.18;
    c.x = cx + Math.cos(angle) * spread;
    c.y = cy + Math.sin(angle) * spread;
  });

  const halfW = areaW / 2;
  const halfH = areaH / 2;
  for (let iter = 0; iter < 80; iter++) {
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        const a = circles[i];
        const b = circles[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const minDist = a.r + b.r + 3;
        if (dist < minDist) {
          const push = ((minDist - dist) / dist) * 0.4;
          const px = dx * push;
          const py = dy * push;
          a.x -= px;
          a.y -= py;
          b.x += px;
          b.y += py;
        }
      }
    }

    for (const c of circles) {
      c.x += (cx - c.x) * 0.06;
      c.y += (cy - c.y) * 0.06;
    }

    for (const c of circles) {
      const yRatio = (c.y - (cy - halfH)) / areaH;
      const taperFactor = 1 - yRatio * 0.12;
      const localHalfW = halfW * taperFactor;

      if (c.x - c.r < cx - localHalfW) c.x = cx - localHalfW + c.r;
      if (c.x + c.r > cx + localHalfW) c.x = cx + localHalfW - c.r;
      if (c.y - c.r < cy - halfH) c.y = cy - halfH + c.r;
      if (c.y + c.r > cy + halfH) c.y = cy + halfH - c.r;
    }
  }

  return circles;
}

// ── SVG primitives ─────────────────────────────────────────────────

function Basket({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const r = 10;
  const rimH = 7;
  return (
    <g>
      <rect
        x={x - 3}
        y={y - rimH}
        width={w + 6}
        height={rimH}
        rx={3}
        fill="currentColor"
        fillOpacity={0.08}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.18}
      />
      <path
        d={`M ${x} ${y}
            L ${x + 6} ${y + h - r} Q ${x + 6} ${y + h} ${x + 6 + r} ${y + h}
            L ${x + w - 6 - r} ${y + h} Q ${x + w - 6} ${y + h} ${x + w - 6} ${y + h - r}
            L ${x + w} ${y} Z`}
        fill="currentColor"
        fillOpacity={0.02}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.14}
      />
      {[0.3, 0.55, 0.8].map((f) => {
        const ly = y + f * h;
        const inset = 6 + f * 5;
        return (
          <line
            key={f}
            x1={x + inset}
            y1={ly}
            x2={x + w - inset}
            y2={ly}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeOpacity={0.06}
          />
        );
      })}
    </g>
  );
}

function AssetBubble({ cx, cy, r, asset }: { cx: number; cy: number; r: number; asset: Asset }) {
  const t = STRINGS[useDocLocale()];
  const color = TONE_COLOR[asset.tone];
  // Small bubbles: show label + % below the circle
  const isSmall = r < 22;

  if (isSmall) {
    const fontSize = 7;
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
        <text
          x={cx}
          y={cy + 1}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize + 2}
          fill={color}
          fontWeight="700"
        >
          {asset.weight}%
        </text>
        <text x={cx} y={cy + r + 10} textAnchor="middle" fontSize={fontSize} fill={color} fontWeight="600">
          {t.assets[asset.tone]}
        </text>
      </g>
    );
  }

  const labelSize = r > 32 ? 10 : 9;
  const pctSize = r > 32 ? 12 : 10;

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
      <text
        x={cx}
        y={cy - pctSize * 0.4}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={labelSize}
        fill={color}
        fontWeight="600"
      >
        {t.assets[asset.tone]}
      </text>
      <text
        x={cx}
        y={cy + labelSize * 0.7}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={pctSize}
        fill={color}
        fontWeight="700"
      >
        {asset.weight}%
      </text>
    </g>
  );
}

function PersonIcon({ cx, cy, size }: { cx: number; cy: number; size: number }) {
  const headR = size * 0.28;
  const bodyW = size * 0.6;
  const bodyH = size * 0.35;
  const headY = cy - size * 0.18;
  const bodyY = headY + headR + size * 0.06;
  return (
    <g>
      <circle cx={cx} cy={headY} r={headR} fill={PERSONA_COLOR} />
      <rect x={cx - bodyW / 2} y={bodyY} width={bodyW} height={bodyH} rx={bodyW / 2} fill={PERSONA_COLOR} />
    </g>
  );
}

function RiskBadge({ x, y, risk }: { x: number; y: number; risk: PersonaRisk }) {
  const t = STRINGS[useDocLocale()];
  const color = PERSONA_RISK_COLOR[risk];
  const label = t.riskBadge(t.riskLevel[risk]);
  const w = 90;
  const h = 15;
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y}
        width={w}
        height={h}
        rx={h / 2}
        fill={color}
        fillOpacity={0.14}
        stroke={color}
        strokeWidth="0.8"
      />
      <text x={x} y={y + h / 2 + 3.5} textAnchor="middle" fontSize="7.5" fill={color} fontWeight="600">
        {label}
      </text>
    </g>
  );
}

// ── Single persona basket ──────────────────────────────────────────

function PersonaBasket({
  persona,
  info,
  x,
  y,
  w,
}: {
  persona: Persona;
  info: (typeof STRINGS)[Locale]["personas"][number];
  x: number;
  y: number;
  w: number;
}) {
  const t = STRINGS[useDocLocale()];
  const basketW = w - 12;
  const basketH = 180;
  const basketX = x + 6;
  const basketY = y + 92;

  const basketCx = basketX + basketW / 2;
  const basketCy = basketY + basketH / 2;
  const circles = packCircles(persona.assets, basketCx, basketCy, basketW - 20, basketH - 24, 10, 48);

  return (
    <g>
      <PersonIcon cx={x + w / 2} cy={y + 12} size={24} />
      <text x={x + w / 2} y={y + 34} textAnchor="middle" fontSize="11" fill={PERSONA_COLOR} fontWeight="700">
        {t.nameWithAge(info.name, info.age)}
      </text>
      <text x={x + w / 2} y={y + 48} textAnchor="middle" fontSize="8.5" fill="currentColor">
        {info.situation}
      </text>

      <text x={x + w / 2} y={y + 58} textAnchor="middle" fontSize="8.5" fill="currentColor">
        {t.goalWithHorizon(info.goal, info.horizon)}
      </text>

      <RiskBadge x={x + w / 2} y={y + 65} risk={persona.risk} />
      <Basket x={basketX} y={basketY} w={basketW} h={basketH} />

      {circles.map((c) => (
        <AssetBubble key={c.asset.tone} cx={c.x} cy={c.y} r={c.r} asset={c.asset} />
      ))}
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function PersonaBasketsIllustration() {
  const t = STRINGS[useDocLocale()];
  const colW = 190;
  const gap = 16;
  const vw = colW * 3 + gap * 2 + 20;
  const vh = 314;

  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} className="mx-auto w-full max-w-3xl">
      {personas.map((p, i) => (
        <PersonaBasket
          key={t.personas[i]!.name}
          persona={p}
          info={t.personas[i]!}
          x={10 + i * (colW + gap)}
          y={6}
          w={colW}
        />
      ))}
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocPortfolioPersonaBaskets() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <PersonaBasketsIllustration />
    </IllustrationCard>
  );
}
